import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { User, Search, Plus } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PrivateCustomerDialog = ({ open, onClose, onSelectCustomer }) => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    idNumber: '',
    phone: '',
    email: '',
    address: ''
  });

  useEffect(() => {
    if (open) {
      loadCustomers();
      setSearchTerm('');
      setShowAddForm(false);
    }
  }, [open]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = customers.filter(c => 
        c.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.lastName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchTerm, customers]);

  const loadCustomers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/private-customers`);
      const data = await response.json();
      setCustomers(data);
      setFilteredCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const handleSelectCustomer = (customer) => {
    const fullName = `${customer.firstName} ${customer.lastName}`;
    onSelectCustomer(fullName);
    onClose();
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.idNumber || !formData.phone) {
      toast({
        title: 'שגיאה',
        description: 'נא למלא את כל השדות החובה',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/private-customers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const newCustomer = await response.json();
        toast({
          title: 'הצלחה!',
          description: 'לקוח פרטי נוסף בהצלחה'
        });
        const fullName = `${newCustomer.firstName} ${newCustomer.lastName}`;
        onSelectCustomer(fullName);
        onClose();
      }
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'אירעה שגיאה בשמירת הלקוח',
        variant: 'destructive'
      });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <User className="w-6 h-6 text-purple-600" />
            בחירת לקוח פרטי
          </DialogTitle>
        </DialogHeader>

        {!showAddForm ? (
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="חפש לקוח..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-10 py-6 text-lg"
              />
            </div>

            {/* Add New Button */}
            <Button
              onClick={() => setShowAddForm(true)}
              className="w-full py-6 bg-gradient-to-r from-purple-600 to-pink-600"
            >
              <Plus className="ml-2 w-5 h-5" />
              הוסף לקוח פרטי חדש
            </Button>

            {/* Customers List */}
            <div className="border rounded-lg max-h-[400px] overflow-y-auto">
              {filteredCustomers.length > 0 ? (
                <div className="divide-y">
                  {filteredCustomers.map((customer) => (
                    <div
                      key={customer.id}
                      onClick={() => handleSelectCustomer(customer)}
                      className="p-4 hover:bg-purple-50 cursor-pointer transition-colors"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-lg text-purple-900">
                            {customer.firstName} {customer.lastName}
                          </h3>
                          <p className="text-sm text-gray-600">טלפון: {customer.phone}</p>
                          {customer.email && (
                            <p className="text-sm text-gray-600">אימייל: {customer.email}</p>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          ת.ז. {customer.idNumber}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-gray-500">
                  {searchTerm ? 'לא נמצאו לקוחות' : 'אין לקוחות פרטיים במערכת'}
                </div>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleAddCustomer} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">שם פרטי *</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="הזן שם פרטי"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">שם משפחה *</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="הזן שם משפחה"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="idNumber">תעודת זהות *</Label>
                <Input
                  id="idNumber"
                  name="idNumber"
                  value={formData.idNumber}
                  onChange={handleChange}
                  placeholder="הזן מספר ת.ז."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">טלפון *</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="הזן מספר טלפון"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">אימייל</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="הזן כתובת אימייל"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">כתובת</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="הזן כתובת"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600">
                הוסף ובחר
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
                ביטול
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PrivateCustomerDialog;
